import React, { useRef } from 'react';
import { Loading } from '@/components/Loading';
import { IUserSchema, UserService } from '@/api/user';
import { UserInfo } from '@/components/user/UserInfo';
import { AccessType, StatusUser } from '@/utils/constants/users';
import { useCustomSpec } from '@/utils/hooks/useCustomSpec';
import {
  ExtendedTableLowCode,
  ExtendedTableLowCodeRef,
} from '@/components/shared/table/ExtendedTableLowCode';
import { IFilterSpec } from '@/components/shared/filter/TableFilterPanel';
import { ISelectOption } from '@/components/form/fields/SelectField';
import { LinkButton } from '@/components/shared/button/LinkButton';
import { HideCustomCreateButton } from '@/components/shared/button/HideCustomButtonCreate';
import { FaPen } from 'react-icons/fa6';

interface CustomActionCellProps {
  data: IUserSchema;
}

export const CustomActionCell: React.FC<CustomActionCellProps> = ({ data }) => {
  return (
    <LinkButton path={`${data.id}/edit`} icon={<FaPen size={15} />} />
  );
};

export const ListUser: React.FC = () => {
  const { data, isLoading } = UserService.useGetSummary();
  const ref = useRef<ExtendedTableLowCodeRef>(null);

  const spec = useCustomSpec('/api/crud/user', {
    columns: [
      { key: 'first_name', label: 'Name' },
      { key: 'email', label: 'Email' },
      { key: 'user_type', label: 'User Type' },
      { key: 'status_user', label: 'Status' },
      { key: 'achievement', label: 'Achievement' },
    ],
    title: 'User Management',
  });

  const renderCustomCell = ({ name, rowValue, defaultCell }: any) => {
    if (name === 'first_name') {
      return <>{`${rowValue.first_name} ${rowValue.last_name}`}</>;
    }

    if (name === 'achievement') {
      const achievementPercentage = (rowValue.achievement * 100).toFixed(2);
      return <>{`${achievementPercentage}%`}</>;
    }

    if (name === 'status_user') {
      return rowValue.status_user ? (
        <span className="font-bold text-green-500 uppercase">
          {StatusUser.ACTIVE}
        </span>
      ) : (
        <span className="font-bold text-yellow-500 uppercase">
          {StatusUser.INACTIVE}
        </span>
      );
    }

    return defaultCell;
  };

  const statusUserOptions: ISelectOption[] = StatusUser.toOptions();
  const accessTypeOptions: ISelectOption[] = AccessType.toOptions();

  const filterSpec: IFilterSpec[] = [
    { name: 'first_name', label: 'Name', type: 'text' },
    { name: 'email', label: 'Email', type: 'text' },
    {
      name: 'user_type',
      label: 'User Type',
      type: 'option',
      options: accessTypeOptions,
    },
    {
      name: 'status_user',
      label: 'Status',
      type: 'option',
      options: statusUserOptions,
    },
    {
      name: 'achievement',
      label: 'Achievement',
      type: 'number',
      placeholder: 'Masukkan nilai persentase',
      step: 1,
    },
  ];

  const SubHeader = () => {
    return (
      <div className="flex flex-row gap-2 py-5 px-7">
        <UserInfo label="Total Users" value={data?.total} />
        <UserInfo label="Active Users" value={data?.active} />
        <UserInfo label="Inactive Users" value={data?.inactive} />
      </div>
    );
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <div className="px-4">
      <ExtendedTableLowCode
        ref={ref}
        spec={spec}
        defaultSort="first_name"
        defaultSortOrder="asc"
        subHeader={() => <SubHeader />}
        customCell={renderCustomCell}
        customActionCell={data => (
          <CustomActionCell data={data as IUserSchema} />
        )}
        customButtonCreate={HideCustomCreateButton}
        showFilter
        filterSpec={filterSpec}
      />
    </div>
  );
};